// One product row in the Image Studio catalog grid.
//
// Lighter than the retired /image-gen ProductRow — no inline persona
// picking, no inline Generate button, no expand/collapse. The chat
// drawer owns the action; the grid just surfaces coverage + lets the
// operator pick which products to talk about.

import { Badge, Box, Checkbox, HStack, Image, Progress, Text, VStack } from '@chakra-ui/react';
import type { SummaryProduct } from './api';

export function CoverageRow({
  product, isSelected, onToggleSelect,
}: {
  product:        SummaryProduct;
  isSelected:     boolean;
  onToggleSelect: () => void;
}) {
  const covered = product.coveredPersonaCount;
  const total   = product.totalPersonas;
  const pct     = total > 0 ? Math.round((covered / total) * 100) : 0;
  const latest  = product.latestGeneratedAt
    ? new Date(product.latestGeneratedAt).toLocaleDateString()
    : null;

  return (
    <Box
      borderWidth="1px"
      borderColor={isSelected ? 'purple.400' : 'brand.border'}
      bg={isSelected ? 'purple.50' : 'white'}
      borderRadius="md"
      transition="background-color 0.1s"
      _hover={{ borderColor: isSelected ? 'purple.500' : 'purple.300' }}
    >
      <HStack
        px={3}
        py={2.5}
        spacing={3}
        align="center"
        cursor="pointer"
        onClick={onToggleSelect}
      >
        <Checkbox
          isChecked={isSelected}
          onChange={onToggleSelect}
          onClick={(e) => e.stopPropagation()}
        />
        <Box
          w="40px"
          h="40px"
          borderRadius="sm"
          overflow="hidden"
          flexShrink={0}
          bg="gray.100"
        >
          {product.heroUrl && (
            <Image src={product.heroUrl} w="100%" h="100%" objectFit="cover" fallbackSrc="" />
          )}
        </Box>
        <VStack align="flex-start" spacing={0} flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="600" noOfLines={1}>
            {product.title || `Product ${product.productId.slice(-6)}`}
          </Text>
          {product.categoryName && (
            <Text fontSize="11px" color="brand.muted" noOfLines={1}>{product.categoryName}</Text>
          )}
        </VStack>
        <VStack align="flex-end" spacing={0.5} minW="130px">
          <HStack spacing={2} fontSize="11px" color="brand.muted">
            <Text>{covered} / {total}</Text>
            <Badge
              size="sm"
              colorScheme={covered === 0 ? 'red' : covered === total ? 'green' : 'orange'}
              variant="subtle"
            >
              {pct}%
            </Badge>
          </HStack>
          <Progress
            value={pct}
            size="xs"
            w="110px"
            colorScheme={covered === 0 ? 'red' : covered === total ? 'green' : 'orange'}
            borderRadius="full"
          />
          {latest && (
            <Text fontSize="10px" color="brand.muted">last: {latest}</Text>
          )}
        </VStack>
      </HStack>
    </Box>
  );
}
